import { useEffect, useRef } from 'react';
import DailyIframe, { type DailyCall } from '@daily-co/daily-js';
import { cn } from '@/lib/utils';

/**
 * Embeds the Daily.co prebuilt call UI in an iframe inside our app.
 * Handles lifecycle: creates the call object on mount with a meeting token,
 * tears it down on unmount, and notifies the parent when the user leaves.
 *
 * DECISION: we use Daily's prebuilt frame (not custom UI via @daily-co/daily-react)
 * because it ships with full controls (mic/cam/screenshare/leave/quality) and
 * standardized layouts. We only need entry + exit hooks for the MVP.
 */
export function CallRoom({
  roomUrl,
  meetingToken,
  userName,
  onLeft,
  className,
}: {
  roomUrl: string;
  meetingToken?: string;
  userName?: string;
  onLeft?: () => void;
  className?: string;
}) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const callRef = useRef<DailyCall | null>(null);

  useEffect(() => {
    if (!containerRef.current) return undefined;

    const call = DailyIframe.createFrame(containerRef.current, {
      iframeStyle: {
        width: '100%',
        height: '100%',
        border: 'none',
        backgroundColor: '#0A0A0A',
      },
      showLeaveButton: true,
      showFullscreenButton: true,
    });
    callRef.current = call;

    void call
      .join({
        url: roomUrl,
        ...(meetingToken ? { token: meetingToken } : {}),
        ...(userName ? { userName } : {}),
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error('Daily.co join failed', err);
      });

    const handleLeft = () => onLeft?.();
    call.on('left-meeting', handleLeft);

    return () => {
      call.off('left-meeting', handleLeft);
      void call.destroy();
      callRef.current = null;
    };
    // We intentionally do NOT include onLeft in deps — we want the iframe
    // to live for the lifetime of the dialog. Re-creating the call when
    // a parent re-renders would drop the user from the meeting.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomUrl, meetingToken, userName]);

  return <div ref={containerRef} className={cn('h-full w-full bg-background', className)} />;
}
